import { useAppController } from 'customHooks/useAppController'
import { newProjectActions } from 'features/BillOfMaterials/store/projectReducer'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { PdfViewerModel } from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppDispatch } from 'store/configureStore'
import { ProjectState } from 'store/Project/ProjectTypes'
import { useShowException } from 'utils/useShowException'
import { PdfViewerController } from '../AttachmentsController'

export const usePdfViewModel = (
  bomItemId: string,
  filterByFileName?: string,
  open: boolean = false,
) => {
  const [pdfViewModel, setPdfViewModel] = useState<PdfViewerModel>(null)

  const { controller, loading } = useAppController(
    () => new PdfViewerController(),
  )

  const dispatch = useAppDispatch()

  const ShowException = useShowException('viewer')

  const getViewModel = useCallback(async () => {
    try {
      if (!bomItemId || !open) {
        return null
      }

      const pdfViewModel = await controller.GetPDFViewer(bomItemId)

      if (filterByFileName) {
        pdfViewModel.pages = pdfViewModel.pages.filter((page) =>
          page.originalFileName.includes(filterByFileName),
        )
      }

      dispatch(
        newProjectActions.setProjectStateProperties({
          currentCheckList: {
            shallUpdateCheckList: true,
          },
        } as ProjectState),
      )

      return pdfViewModel
    } catch (err) {
      ShowException(err)
      return null
    }
  }, [ShowException, bomItemId, controller, dispatch, filterByFileName, open])

  const debouncedGetViewModel = useMemo(
    () => debounce(getViewModel, 500, { leading: true, trailing: true }),
    [getViewModel],
  )

  const getPDFViewModel = useCallback(async () => {
    try {
      const pdfViewModel = await debouncedGetViewModel()
      setPdfViewModel(pdfViewModel)
    } catch (err) {}
  }, [debouncedGetViewModel])

  useEffect(() => {
    getPDFViewModel()

    return () => {
      controller.CancelRequests()
      setPdfViewModel(null)
    }
  }, [controller, getPDFViewModel])

  const reloadPdfViewerModel = useCallback(async () => {
    const newPdfViewModel = await getViewModel()
    setPdfViewModel(newPdfViewModel)

    return newPdfViewModel
  }, [getViewModel])

  const handleDeletePdfPage = useCallback(
    async (pageId: string) => {
      try {
        const resp = await controller.DeletePdfPage(bomItemId, pageId)

        if (resp) {
          setPdfViewModel(resp)
        }
      } catch (err) {
        ShowException(err)
      }
    },
    [ShowException, bomItemId, controller],
  )

  return {
    pdfViewModel,
    setPdfViewModel,
    loading,
    reloadPdfViewerModel,
    debouncedGetViewModel,
    handleDeletePdfPage,
  }
}
